/* eslint-disable @typescript-eslint/no-explicit-any */
import CategorySort from '@/components/Category/CategorySort'
import ImageOptimize from '@/components/ImageOptimize'
import ProductCard from '@/components/ProductCard'
import ProductCardSkeleton from '@/components/Skeleton/ProductCardSkeleton'
import { useGetProductsByCategory } from '@/hooks/useProductHook'
import { Meta } from '@/layouts/Meta'
import { Main } from '@/templates/Main'
import { type Product, type ProductResponse, type QueryParam } from '@/types'
import { setContext } from '@/utils/api-interceptors'
import {
  getBanner,
  getBottomMenu,
  getConfig,
  getMenus,
  getProductWithPaginate,
  getQuickMenu,
  getSaleRegion,
} from '@/utils/endpoint'
import { useSearchParams } from '@/utils/next-util'
import { getCookies } from 'cookies-next'
import { type GetServerSideProps, type GetServerSidePropsContext } from 'next'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { v4 as uuidv4 } from 'uuid'
export const sortByTypes = [
  { label: 'Nên mua', type: 'custom', name: 'shouldBuy' },
  { label: 'Bán chạy', type: 'custom', name: 'bestSelling' },
  { label: 'Trả góp', type: 'custom', name: 'allowInstallment' },
  { label: 'Giá tăng dần', type: 'sortType', name: 'asc' },
  { label: 'Giá giảm dần', type: 'sortType', name: 'desc' },
]
const SearchPage = ({
  productSearchData,
}: {
  productSearchData: ProductResponse
}) => {
  const t = useTranslations('Common')
  const router = useRouter()
  const [totalProduct, setTotalProduct] = useState<number>(
    productSearchData?.total || 0
  )
  const [products, setProducts] = useState<Product[]>(productSearchData?.data)
  const { getProducts } = useGetProductsByCategory()
  const [totalPages, setTotalPages] = useState(productSearchData?.pages)
  const [routerChange, setRouterChange] = useState(false)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [currentParams, setCurrentParams] = useSearchParams([
    {
      name: 'page',
      value: null,
    },
    {
      name: 'query',
      value: null,
    },
    {
      name: 'sortBy',
      value: null,
    },
    {
      name: 'sortType',
      value: null,
    },
    {
      name: 'bestSelling',
      value: null,
    },
    {
      name: 'shouldBuy',
      value: null,
    },
    {
      name: 'allowInstallment',
      value: null,
    },
  ])
  useEffect(() => {
    if (productSearchData?.data) {
      setProducts(productSearchData?.data)
      setTotalProduct(productSearchData?.total)
    }
    return () => {
      setCurrentPage(1)
    }
  }, [productSearchData])

  const [sortByOptions] = useState(sortByTypes)
  const activeParams = currentParams
  const activeSort = activeParams.find(
    (filter: any) => filter.name === 'sortType'
  ).value
  const activeSortCustom = activeParams
    .filter((filter: any) => filter.value === 'true')
    .map((item: { name: string }) => item.name) || ['']
  const updateParams = useCallback(
    (params: any, routerChange = true) => {
      setLoading(true)
      setCurrentPage(1)
      setProducts([])
      if (routerChange) {
        setRouterChange(true)
      }
      const newFilters = activeParams.map((filter: any) => {
        const matchedParam = params?.find((p: any) => p.name === filter.name)
        return matchedParam ? { ...filter, value: matchedParam?.value } : filter
      })
      setCurrentParams(newFilters)
    },
    [activeParams]
  )
  useEffect(() => {
    if (!routerChange) {
      return
    }
    setLoading(true)
    setProducts([])
    if (router) {
      getProducts(
        {
          limit: 16,
          currentPage: currentPage,
          sortBy: (router?.query.sortBy as string) || 'score',
          sortType: (router?.query.sortType as string) || 'desc',
          search: router?.query?.query,
        },
        {
          onSuccess: res => {
            setProducts(res.data.data)
            setLoading(false)
            setTotalPages(res.data.pages)
            setTotalProduct(res.data.total)
          },
        }
      )
    }
  }, [router.query])
  const fetchMoreProducts = () => {
    setCurrentPage(currentPage + 1)
    setLoading(true)
    getProducts(
      {
        limit: 16,
        currentPage: currentPage + 1,
        sortBy: (router?.query.sortBy as string) || 'score',
        sortType: (router?.query.sortType as string) || 'desc',
        search: router?.query?.query,
      },
      {
        onSuccess: res => {
          setProducts(products.concat(res.data.data))
          setLoading(false)
          setTotalPages(res.data.pages)
        },
      }
    )
  }
  return (
    <Main meta={<Meta title={'Search'} description={'description'} />}>
      <section className='ProductSearchResult pb-[120px]'>
        <h3 className='mb-3 mt-1 text-base font-bold text-dark-400'>
          Kết quả tìm kiếm cho từ khóa “{router?.query?.query}”
        </h3>
        <div className='gap flex items-center gap-4 text-sm'>
          <h1>
            <strong>{totalProduct}</strong> <span>{t('product')}</span>
          </h1>
          <CategorySort
            sortOptions={sortByOptions}
            onChange={updateParams}
            activeSort={activeSort}
            activeSortCustom={activeSortCustom}
          />
        </div>
        {loading && (
          <div className='products mt-4 grid grid-cols-4 gap-[20px]'>
            {[...new Array(12)].map(() => (
              <ProductCardSkeleton key={uuidv4()} />
            ))}
          </div>
        )}
        {products && products.length > 0 ? (
          <InfiniteScroll
            dataLength={products.length}
            next={fetchMoreProducts}
            hasMore={
              currentPage !== Number(totalPages) || Number(totalPages) === 0
            }
            loader=''
          >
            <div className='productData mt-4 grid grid-cols-4 gap-5'>
              {products.map((product: Product, idx: number) => (
                <ProductCard
                  layout={4}
                  product={product}
                  key={product._id + idx + 'sr'}
                />
              ))}
            </div>
          </InfiniteScroll>
        ) : (
          ''
        )}
        {products && products.length == 0 && !loading && (
          <div className='flex flex-col items-center justify-center pt-16'>
            <ImageOptimize
              src='/no-product.svg'
              alt={t('notFoundProductFit')}
              width={500}
              height={372}
            />
            <span className='text-HeadlineMedium mt-4 font-normal'>
              {t('notFoundProductFit')}
            </span>
          </div>
        )}
      </section>
    </Main>
  )
}

export default SearchPage

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { req, res, query } = context

  const params = {
    ...query,
    page: 1,
    fullTextSearch: query?.query,
    sortBy: query?.sortBy ? query?.sortBy : 'score',
    sortType: query?.sortType ? query?.sortType : 'desc',
    limit: 16,
  }

  const { token, language, messages } = await setContext(
    getCookies({ req, res }),
    {
      language: context.locale,
    }
  )

  const [
    regions,
    menus,
    banner,
    quickMenu,
    productSearchData,
    bottomMenu,
    systemConfig,
  ] = await Promise.all([
    getSaleRegion(),
    getMenus(),
    getBanner(),
    getQuickMenu(),
    getProductWithPaginate(params as QueryParam),
    getBottomMenu(),
    getConfig(),
  ])

  return {
    props: {
      regions,
      menus,
      banner,
      language,
      messages,
      quickMenu,
      bottomMenu,
      productSearchData,
      systemConfig,
      token: token || null,
    },
  }
}
